import React from "react";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import { LatexText } from "../../../components/ui/latex-text";
import type { UnitNotes } from "../types";

const calculusAbUnit8Notes: UnitNotes = {
  unitId: "unit-8",
  unitName: "Unit 8: Applications of Integration",
  examWeight: "10–15%",
  focus: "Change (CHA)",
  sections: [
    {
      id: "topic-8-1",
      title: "Topic 8.1: Finding the Average Value of a Function on an Interval",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Geometric Concept</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              In geometry, the area of a rectangle is <LatexText text="$A = \text{Width} \cdot \text{Height}$" />. Therefore, <LatexText text="$\text{Height} = \frac{\text{Area}}{\text{Width}}$" />.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              In calculus, we treat the "Area" under a curve as the integral <LatexText text="$\int_a^b f(x) dx$" /> and the "Width" as the interval length <LatexText text="$(b-a)$" />.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Formula</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The <strong>Average Value</strong> of a continuous function <LatexText text="$f$" /> on the interval <LatexText text="$[a, b]$" /> is:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <LatexText text="$$f_{\text{avg}} = \frac{1}{b-a} \int_{a}^{b} f(x) \, dx$$" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Average Value vs. Average Rate of Change</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              This is a major confusion point for students.
            </p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Average Rate of Change (Slope)</h4>
                <p className="text-sm text-slate-700 mt-1"><LatexText text="$\frac{f(b) - f(a)}{b - a}$" />. This measures how fast the function changes (Secant Slope).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Average Value (Height)</h4>
                <p className="text-sm text-slate-700 mt-1"><LatexText text="$\frac{1}{b-a}\int_a^b f(x) dx$" />. This measures the "average height" of the function itself.</p>
              </div>
            </div>

            <ImportantNote>
              <strong>AP Exam Tip:</strong> If a problem asks for the "Average Temperature," you integrate the Temperature function. If it asks for the "Average Rate at which Temperature is changing," you integrate the <em>derivative</em> of Temperature (which simplifies to average rate of change).
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-2",
      title: "Topic 8.2: Connecting Position, Velocity, and Acceleration of Functions Using Integrals",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Displacement vs. Distance</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              When integrating velocity <LatexText text="$v(t)$" /> over time <LatexText text="$[t_1, t_2]$" />:
            </p>
            <div className="space-y-3 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Displacement (Net Change in Position)</h4>
                <LatexText text="$$\int_{t_1}^{t_2} v(t) \, dt = s(t_2) - s(t_1)$$" />
                <p className="text-sm text-slate-700 mt-2">This accounts for direction. Moving forward (+5) and backward (-5) results in 0 displacement.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Total Distance Traveled</h4>
                <LatexText text="$$\int_{t_1}^{t_2} |v(t)| \, dt$$" />
                <p className="text-sm text-slate-700 mt-2">This makes all movement positive. Moving forward (+5) and backward (-5) results in 10 distance.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Finding Current Position</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To find the position of a particle at a specific time <LatexText text="$t$" />, you need the <strong>Initial Position</strong> and the <strong>Displacement</strong>.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <LatexText text="$$\text{Position}(t) = \text{Position}(0) + \int_{0}^{t} v(x) \, dx$$" />
              <LatexText text="$$s(t) = s(0) + \int_{0}^{t} v(x) \, dx$$" />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-3",
      title: "Topic 8.3: Using Accumulation Functions and Definite Integrals in Applied Contexts",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The "Rate In / Rate Out" Model</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Many FRQs involve a tank, a line of people, or a processing plant.
            </p>
            <div className="space-y-2 mb-4">
              <p className="text-slate-700">Let <LatexText text="$E(t)$" /> be the rate entering.</p>
              <p className="text-slate-700">Let <LatexText text="$L(t)$" /> be the rate leaving.</p>
              <p className="text-slate-700">Let <LatexText text="$A(t)$" /> be the total Amount at time <LatexText text="$t$" />.</p>
            </div>

            <p className="text-slate-700 leading-relaxed mb-4">
              The <strong>Total Amount Equation</strong> is:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <LatexText text="$$A(t) = \text{Initial Amount} + \int_{0}^{t} E(x) \, dx - \int_{0}^{t} L(x) \, dx$$" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Analysis of the Model</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Is the amount increasing?</h4>
                <p className="text-sm text-slate-700 mt-1">Check if <LatexText text="$E(t) > L(t)$" />.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Is the amount decreasing?</h4>
                <p className="text-sm text-slate-700 mt-1">Check if <LatexText text="$L(t) > E(t)$" />.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Absolute Max/Min Amount</h4>
                <p className="text-sm text-slate-700 mt-1">Use the Candidates Test.</p>
                <ol className="list-decimal pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li>Find critical points: Set <LatexText text="$A'(t) = E(t) - L(t) = 0$" />.</li>
                  <li>Check critical points and endpoints in the <LatexText text="$A(t)$" /> equation.</li>
                </ol>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-4",
      title: "Topics 8.4 – 8.6: Finding the Area Between Curves",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Vertical Slicing (Functions of x)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              When curves are defined as <LatexText text="$y = f(x)$" /> and <LatexText text="$y = g(x)$" />, we use vertical rectangles with width <LatexText text="$dx$" />.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <LatexText text="$$\text{Area} = \int_{a}^{b} (\text{Top Curve} - \text{Bottom Curve}) \, dx$$" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Horizontal Slicing (Functions of y)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              When curves are defined as <LatexText text="$x = f(y)$" /> and <LatexText text="$x = g(y)$" />, it is often easier to use horizontal rectangles with width <LatexText text="$dy$" />.
            </p>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <LatexText text="$$\text{Area} = \int_{c}^{d} (\text{Right Curve} - \text{Left Curve}) \, dy$$" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Multiple Intersections</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If two curves cross each other multiple times (e.g., sine and cosine), the "Top" curve switches.
            </p>
            <div className="space-y-2 mb-4">
              <p className="text-slate-700">You must find the intersection points.</p>
              <p className="text-slate-700">You must split the integral into separate parts.</p>
              <p className="text-slate-700"><em>Alternatively:</em> <LatexText text="$\int_{a}^{b} |f(x) - g(x)| \, dx$" /></p>
            </div>

            <ImportantNote>
              <strong>AP Exam Tip:</strong> Always sketch the graph (or use your calculator) to identify which function is Top/Right and which is Bottom/Left.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-7",
      title: "Topics 8.7 – 8.8: Volumes with Cross Sections",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Concept (Slicing a Loaf of Bread)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We are building a 3D solid where the base is a flat region on the <LatexText text="$xy$" />-plane (Area Between Curves), and the 3D shape pops out of the paper.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <LatexText text="$$\text{Volume} = \int_{a}^{b} \text{Area of Cross Section}(x) \, dx$$" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Strategy</h3>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Draw the Base</h4>
                  <p className="text-sm text-slate-600 mt-1">Sketch the region bounded by the curves</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Draw a Slice</h4>
                  <p className="text-sm text-slate-600 mt-1">Draw a representative line segment across the region (usually perpendicular to the x-axis). Let <LatexText text="$s$" /> be the length of this segment (<LatexText text="$s = \text{Top} - \text{Bottom}$" />)</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Identify the Formula</h4>
                  <p className="text-sm text-slate-600 mt-1">Determine the area formula <LatexText text="$A(x)$" /> based on the shape described</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Integrate</h4>
                  <p className="text-sm text-slate-600 mt-1"><LatexText text="$\int A(x) dx$" /></p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Common Cross-Section Formulas</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If <LatexText text="$s = \text{Top} - \text{Bottom}$" />:
            </p>
            <div className="space-y-2">
              <p className="text-slate-700"><strong>Squares:</strong> <LatexText text="$A = s^2$" /></p>
              <p className="text-slate-700"><strong>Rectangles (height <LatexText text="$h$" />):</strong> <LatexText text="$A = s \cdot h$" /></p>
              <p className="text-slate-700"><strong>Semicircles:</strong> <LatexText text="$A = \frac{\pi}{8} s^2$" /> (Why 8? Area is <LatexText text="$\frac{1}{2}\pi r^2$" />, and radius <LatexText text="$r = s/2$" />)</p>
              <p className="text-slate-700"><strong>Equilateral Triangles:</strong> <LatexText text="$A = \frac{\sqrt{3}}{4} s^2$" /></p>
              <p className="text-slate-700"><strong>Isosceles Right Triangles (leg on base):</strong> <LatexText text="$A = \frac{1}{2} s^2$" /></p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-9",
      title: "Topics 8.9 – 8.10: Volume with Disc Method",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Solids of Revolution</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              These solids are formed by taking a flat region and spinning it around an axis (like a lathe).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Disc Method (No Gap)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Use this when the region touches the axis of revolution everywhere (no hole in the solid). The cross-section is a circle with area <LatexText text="$\pi R^2$" />.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <LatexText text="$$\text{Volume} = \pi \int_{a}^{b} [R(x)]^2 \, dx$$" />
              <p className="text-sm text-slate-700 mt-3"><strong><LatexText text="$R(x)$" />:</strong> The distance from the axis of revolution to the outer edge of the curve.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Revolving Around Other Axes</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The radius <LatexText text="$R$" /> is not always just <LatexText text="$f(x)$" />. It is the distance from the function to the axis.
            </p>
            <div className="space-y-2">
              <p className="text-slate-700">If revolving region under <LatexText text="$f(x)$" /> around line <LatexText text="$y = -2$" />: <LatexText text="$R = f(x) - (-2) = f(x) + 2$" /></p>
              <p className="text-slate-700">If revolving region under <LatexText text="$f(x)$" /> around line <LatexText text="$y = 5$" />: <LatexText text="$R = 5 - f(x)$" /></p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-11",
      title: "Topics 8.11 – 8.12: Volume with Washer Method",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Washer Method (With Gap)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Use this when there is a space between the region and the axis of revolution (the solid has a hole/tunnel through it). The cross-section is a "washer" (a ring).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Formula</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <LatexText text="$$\text{Volume} = \pi \int_{a}^{b} \left( [R(x)]^2 - [r(x)]^2 \right) \, dx$$" />
            </div>
            <div className="space-y-2">
              <p className="text-slate-700"><strong><LatexText text="$R(x)$" /> (Outer Radius):</strong> Distance from axis of revolution to the <strong>far</strong> curve.</p>
              <p className="text-slate-700"><strong><LatexText text="$r(x)$" /> (Inner Radius):</strong> Distance from axis of revolution to the <strong>near</strong> curve.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Common Mistake</h3>
            <div className="space-y-2">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-sm text-slate-700"><strong>Correct:</strong> <LatexText text="$\int (R^2 - r^2) dx$" /></p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="text-sm text-slate-700"><strong>Incorrect:</strong> <LatexText text="$\int (R - r)^2 dx$" /> (This is Algebraically wrong!)</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Choosing dx or dy</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Vertical Axis of Revolution</h4>
                <p className="text-sm text-slate-700 mt-1">Usually requires <LatexText text="$dy$" /> integrals (everything in terms of <LatexText text="$y$" />, limits are <LatexText text="$y$" />-values).</p>
                <p className="text-sm text-slate-700 mt-2">Radius = Right - Axis (or Axis - Left).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Horizontal Axis of Revolution</h4>
                <p className="text-sm text-slate-700 mt-1">Usually requires <LatexText text="$dx$" /> integrals (everything in terms of <LatexText text="$x$" />, limits are <LatexText text="$x$" />-values).</p>
                <p className="text-sm text-slate-700 mt-2">Radius = Top - Axis (or Axis - Bottom).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "summary-8",
      title: "Summary of Key Skills for Unit 8",
      content: (
        <div className="space-y-6">
          <section>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <span className="bg-blue-200 text-blue-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Reading Context</h4>
                  <p className="text-sm text-slate-700 mt-1">Distinguishing between "Average Value" (integral of function) and "Average Rate of Change" (integral of derivative / slope).</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="bg-green-200 text-green-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Position Modeling</h4>
                  <p className="text-sm text-slate-700 mt-1">Using <LatexText text="$s(t) = \text{Start} + \int v(t)$" /> to find locations.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <span className="bg-purple-200 text-purple-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Area Setup</h4>
                  <p className="text-sm text-slate-700 mt-1">Correctly identifying "Top - Bottom" for <LatexText text="$dx$" /> and "Right - Left" for <LatexText text="$dy$" />.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <span className="bg-amber-200 text-amber-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Volume Geometry</h4>
                  <p className="text-sm text-slate-700 mt-1">Memorizing the specific area formulas for cross-sections (semicircles, triangles) and the washer formula (<LatexText text="$\pi(R^2 - r^2)$" />).</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default calculusAbUnit8Notes;